import { StorefrontProduct, StorefrontProductVariant } from "../models/storefront";
import { MerchColorOption, MerchProduct, MerchVariantOption } from "../models/merch";

function slugify(value: string): string {
	return value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)+/g, "");
}

export function buildMerchProductSlug(product: Pick<StorefrontProduct, "id" | "name">) {
	return `${slugify(product.name)}-${product.id}`;
}

export function parseMerchProductId(slugParam?: string) {
	if (!slugParam) return undefined;
	const match = slugParam.match(/(\d+)\s*$/);
	if (!match) return undefined;
	return Number(match[1]);
}

export function getPrimaryProductImageUrl(
	product: StorefrontProduct,
	baseURL: string
) {
	const primary =
		product.images.find((img) => img.isPrimary) ??
		[...product.images].sort((a, b) => a.sortOrder - b.sortOrder)[0];

	return primary?.url ? `${baseURL}/common/uploads/${primary.url}` : undefined;
}

export function getVariantAttributesByType(variants: StorefrontProductVariant[]) {
	const map = new Map<string, Set<string>>();

	for (const variant of variants) {
		for (const attr of variant.attributes ?? []) {
			const type = attr.attributeType;
			const set = map.get(type) ?? new Set<string>();
			set.add(attr.value);
			map.set(type, set);
		}
	}

	return [...map.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([type, values]) => ({
			name: type,
			values: [...values].sort((a, b) => a.localeCompare(b)),
		}));
}

function colorHexFromName(name: string): string | undefined {
	// No DB changes: we can only infer colors from the label. Keep it predictable and
	// non-random; unknown labels should fall back to a neutral swatch.
	const normalized = name.trim().toLowerCase();
	if (!normalized) return undefined;

	const rules: Array<[RegExp, string]> = [
		[/\b(black|charcoal)\b/, "#111827"],
		[/\b(midnight)\b/, "#000D74"],
		[/\b(graphite)\b/, "#2F2F2F"],
		[/\b(white|cloud|ivory)\b/, "#F8FAFC"],
		[/\b(gray|grey|steel|slate)\b/, "#6B7280"],
		[/\bglacier\b/, "#A5B4FC"],
		[/\bdeep\s*sea\b/, "#0B3B5A"],
		[/\b(navy)\b/, "#000D74"],
		[/\b(ocean|aqua|turquoise)\b/, "#0DAAEC"],
		[/\b(seafoam|mint)\b/, "#0FF1E5"],
		[/\b(blue)\b/, "#0823F8"],
		[/\b(green|forest)\b/, "#16A34A"],
		[/\b(red|crimson)\b/, "#DC2626"],
		[/\b(purple|violet|plum|aurora)\b/, "#470096"],
		[/\b(sand|khaki|tan|beige)\b/, "#D6C5A0"],
	];

	for (const [pattern, hex] of rules) {
		if (pattern.test(normalized)) return hex;
	}

	return undefined;
}

export function getColorOptions(variants: StorefrontProductVariant[]): MerchColorOption[] {
	const colors = new Map<string, string | undefined>();
	for (const variant of variants) {
		for (const attr of variant.attributes ?? []) {
			if (attr.attributeType !== "Color") continue;
			const existing = colors.get(attr.value);
			const hex = attr.hexColor ?? undefined;
			// Prefer an explicit admin-defined hex, but keep the first seen value if already set.
			if (!existing && hex) colors.set(attr.value, hex);
			if (!colors.has(attr.value)) colors.set(attr.value, undefined);
		}
	}

	return [...colors.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([name, hex]) => ({
			name,
			hex:
				hex && /^#[0-9a-fA-F]{6}$/.test(hex)
					? hex
					: colorHexFromName(name),
		}));
}

export function findMatchingVariant(
	variants: StorefrontProductVariant[],
	selected: Record<string, string>
) {
	const required = Object.entries(selected).filter(([, value]) => value);
	if (required.length === 0) {
		// If variants don't have attribute metadata at all (common early-admin setup),
		// fall back to the first active variant so "Add to cart" still works.
		const hasAnyAttributes = variants.some((v) => (v.attributes ?? []).length > 0);
		if (!hasAnyAttributes) {
			return variants.find((v) => v.isActive) ?? variants[0];
		}
		return undefined;
	}

	return variants.find((variant) => {
		const attrs = variant.attributes ?? [];
		return required.every(([type, value]) =>
			attrs.some((a) => a.attributeType === type && a.value === value)
		);
	});
}

export function toMerchProduct(
	product: StorefrontProduct,
	baseURL: string
): MerchProduct {
	const slug = buildMerchProductSlug(product);

	const colorOptions = getColorOptions(product.variants);
	const variantOptions = getVariantAttributesByType(product.variants).filter(
		(v) => v.name !== "Color"
	);

	const image = getPrimaryProductImageUrl(product, baseURL);

	const minPriceModifier = product.variants.length
		? Math.min(...product.variants.map((v) => v.priceModifier ?? 0))
		: 0;

	return {
		id: product.id,
		slug,
		name: product.name,
		tagline: undefined,
		description: product.description ?? undefined,
		price: Number(product.basePrice) + Number(minPriceModifier),
		currencyCode: "AUD",
		colors: colorOptions.map((c) => ({ ...c, image })),
		variants: variantOptions as MerchVariantOption[],
	};
}
